import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetApiIntegrationsSlideDevices,
  usePostApiIntegrationsSlideDevices,
  usePutApiIntegrationsSlideDevicesId,
  useDeleteApiIntegrationsSlideDevicesId,
  usePostApiIntegrationsSlideTest,
  getGetApiIntegrationsSlideDevicesQueryKey,
  type SlideDeviceResponse,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, AlertCircle, Pencil, Trash2, Wifi, Blinds } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Button, Modal, TextField, PasswordField } from '@/components/ui';

const addDeviceSchema = z.object({
  id: z
    .string()
    .min(1, 'ID is required')
    .max(255)
    .regex(/^[a-z0-9.-]+$/, 'ID must be lowercase letters, numbers, hyphens, and periods only'),
  name: z.string().min(1, 'Name is required').max(255),
  host: z.string().min(1, 'Host is required').max(255),
  deviceCode: z.string().min(1, 'Device code is required'),
});

const editDeviceSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  host: z.string().min(1, 'Host is required').max(255),
  deviceCode: z.string().min(1, 'Device code is required'),
});

type AddDeviceFormData = z.infer<typeof addDeviceSchema>;
type EditDeviceFormData = z.infer<typeof editDeviceSchema>;

export function SlideSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useGetApiIntegrationsSlideDevices();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<SlideDeviceResponse | null>(null);
  const [deletingDevice, setDeletingDevice] = useState<SlideDeviceResponse | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  const addMutation = usePostApiIntegrationsSlideDevices();
  const updateMutation = usePutApiIntegrationsSlideDevicesId();
  const deleteMutation = useDeleteApiIntegrationsSlideDevicesId();

  const devices = data?.devices ?? [];

  const invalidateDevices = () => {
    queryClient.invalidateQueries({ queryKey: getGetApiIntegrationsSlideDevicesQueryKey() });
  };

  return (
    <div className="space-y-6">
      {/* Header with back link */}
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Slide
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Manage your Slide window covering devices
            </p>
          </div>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowAddModal(true)}>
            Add Device
          </Button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch devices</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading your Slide devices.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {/* Device List */}
      {!isLoading && !isError && (
        <>
          {devices.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <Blinds className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No devices configured
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                Add your first Slide device to get started
              </p>
              <Button
                className="mt-6"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setShowAddModal(true)}
              >
                Add Device
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {devices.map((device) => (
                <DeviceCard
                  key={device.id}
                  device={device}
                  onEdit={() => setEditingDevice(device)}
                  onDelete={() => setDeletingDevice(device)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Add Device Modal */}
      <AddDeviceModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setAddError(null);
        }}
        onSubmit={async (data) => {
          setAddError(null);
          try {
            await addMutation.mutateAsync({ data });
            setShowAddModal(false);
            invalidateDevices();
          } catch (err) {
            const error = err as { code?: string; message?: string };
            if (error.code === 'DEVICE_UNREACHABLE') {
              setAddError(
                'Could not connect to the device. Please verify the host and device code are correct.'
              );
            } else if (error.code === 'DEVICE_ALREADY_EXISTS') {
              setAddError(error.message ?? 'A device with this ID already exists.');
            } else {
              setAddError('Failed to add device. Please try again.');
            }
          }
        }}
        isLoading={addMutation.isPending}
        error={addError}
      />

      {/* Edit Device Modal */}
      {editingDevice && (
        <EditDeviceModal
          isOpen={true}
          device={editingDevice}
          onClose={() => setEditingDevice(null)}
          onSubmit={async (data) => {
            if (!editingDevice.id) return;
            try {
              await updateMutation.mutateAsync({ id: editingDevice.id, data });
              setEditingDevice(null);
              invalidateDevices();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingDevice && (
        <DeleteDeviceModal
          isOpen={true}
          device={deletingDevice}
          onClose={() => setDeletingDevice(null)}
          onConfirm={async () => {
            if (!deletingDevice.id) return;
            try {
              await deleteMutation.mutateAsync({ id: deletingDevice.id });
              setDeletingDevice(null);
              invalidateDevices();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

interface DeviceCardProps {
  device: SlideDeviceResponse;
  onEdit: () => void;
  onDelete: () => void;
}

function DeviceCard({ device, onEdit, onDelete }: DeviceCardProps) {
  const testMutation = usePostApiIntegrationsSlideTest();
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleTest = async () => {
    if (!device.host || !device.deviceCode) return;
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync({
        data: { host: device.host, deviceCode: device.deviceCode },
      });
      setTestResult({
        success: result.success ?? false,
        message: result.success
          ? `Connected successfully (position: ${result.position ?? 'unknown'}%)`
          : (result.error ?? 'Connection failed'),
      });
    } catch {
      setTestResult({ success: false, message: 'Connection test failed' });
    }
  };

  const getPositionText = () => {
    if (device.position === undefined || device.position === null) return 'Unknown';
    return `${device.position}%`;
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <Blinds className="mt-1 h-5 w-5 flex-shrink-0 text-gray-400 dark:text-zinc-500" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{device.name}</h3>
              <p className="mt-0.5 font-mono text-sm text-gray-500 dark:text-zinc-500">
                {device.id}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleTest}
              disabled={testMutation.isPending}
              title="Test connection"
            >
              {testMutation.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              ) : (
                <Wifi className="h-4 w-4" />
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={onEdit} title="Edit">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} title="Delete">
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>

        {/* Test Result */}
        {testResult && (
          <div
            className={`mt-3 rounded-lg px-3 py-2 text-sm ${
              testResult.success
                ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400'
                : 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'
            }`}
          >
            {testResult.message}
          </div>
        )}

        {/* Metadata Grid */}
        <div className="mt-4 grid grid-cols-2 gap-4 pl-8 sm:grid-cols-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              Host
            </div>
            <div className="mt-1 font-mono text-sm text-gray-900 dark:text-white">
              {device.host}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              Position
            </div>
            <div className="mt-1 text-sm text-gray-900 dark:text-white">{getPositionText()}</div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              Device Code
            </div>
            <div className="mt-1 font-mono text-sm text-gray-900 dark:text-white">
              {device.deviceCode ? '••••••••' : 'Not set'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AddDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AddDeviceFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function AddDeviceModal({ isOpen, onClose, onSubmit, isLoading, error }: AddDeviceModalProps) {
  const testMutation = usePostApiIntegrationsSlideTest();
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<AddDeviceFormData>({
    resolver: zodResolver(addDeviceSchema),
  });

  const host = watch('host');
  const deviceCode = watch('deviceCode');

  const handleClose = () => {
    reset();
    setTestResult(null);
    onClose();
  };

  const handleTest = async () => {
    if (!host || !deviceCode) return;
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync({ data: { host, deviceCode } });
      setTestResult({
        success: result.success ?? false,
        message: result.success
          ? `Connected successfully! Slide ID: ${result.slideId ?? 'unknown'}, Position: ${result.position ?? 'unknown'}%`
          : (result.error ?? 'Connection failed'),
      });
    } catch {
      setTestResult({ success: false, message: 'Connection test failed' });
    }
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="add-device-form" loading={isLoading}>
        Add Device
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Slide Device"
      subtitle="Connect a new Slide window covering controller"
      size="md"
      footer={footerContent}
    >
      <form id="add-device-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Device ID"
          placeholder="living-room-window-coverings"
          error={errors.id?.message}
          {...register('id')}
        />

        <TextField
          label="Name"
          placeholder="Living Room Window Coverings"
          error={errors.name?.message}
          {...register('name')}
        />

        <TextField
          label="Host"
          placeholder="192.168.1.100 or slide-device.local"
          error={errors.host?.message}
          {...register('host')}
        />

        <PasswordField
          label="Device Code"
          placeholder="Enter device code"
          error={errors.deviceCode?.message}
          {...register('deviceCode')}
        />

        {/* Test Connection Button */}
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleTest}
            disabled={!host || !deviceCode || testMutation.isPending}
            leftIcon={
              testMutation.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              ) : (
                <Wifi className="h-4 w-4" />
              )
            }
          >
            Test Connection
          </Button>
        </div>

        {/* Test Result */}
        {testResult && (
          <div
            className={`rounded-lg px-3 py-2 text-sm ${
              testResult.success
                ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400'
                : 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'
            }`}
          >
            {testResult.message}
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

interface EditDeviceModalProps {
  isOpen: boolean;
  device: SlideDeviceResponse;
  onClose: () => void;
  onSubmit: (data: EditDeviceFormData) => Promise<void>;
  isLoading: boolean;
}

function EditDeviceModal({ isOpen, device, onClose, onSubmit, isLoading }: EditDeviceModalProps) {
  const testMutation = usePostApiIntegrationsSlideTest();
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<EditDeviceFormData>({
    resolver: zodResolver(editDeviceSchema),
    defaultValues: {
      name: device.name ?? '',
      host: device.host ?? '',
      deviceCode: device.deviceCode ?? '',
    },
  });

  const host = watch('host');
  const deviceCode = watch('deviceCode');

  const handleClose = () => {
    reset();
    setTestResult(null);
    onClose();
  };

  const handleTest = async () => {
    if (!host || !deviceCode) return;
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync({ data: { host, deviceCode } });
      setTestResult({
        success: result.success ?? false,
        message: result.success
          ? `Connected successfully! Position: ${result.position ?? 'unknown'}%`
          : (result.error ?? 'Connection failed'),
      });
    } catch {
      setTestResult({ success: false, message: 'Connection test failed' });
    }
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="edit-device-form" loading={isLoading}>
        Save Changes
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Device"
      subtitle="Update Slide device settings"
      size="md"
      footer={footerContent}
    >
      <form id="edit-device-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Living Room Window Coverings"
          error={errors.name?.message}
          {...register('name')}
        />

        <TextField
          label="Host"
          placeholder="192.168.1.100 or slide-device.local"
          error={errors.host?.message}
          {...register('host')}
        />

        <PasswordField
          label="Device Code"
          placeholder="Enter new device code"
          error={errors.deviceCode?.message}
          {...register('deviceCode')}
        />

        {/* Test Connection Button */}
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleTest}
            disabled={!host || !deviceCode || testMutation.isPending}
            leftIcon={
              testMutation.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              ) : (
                <Wifi className="h-4 w-4" />
              )
            }
          >
            Test Connection
          </Button>
        </div>

        {/* Test Result */}
        {testResult && (
          <div
            className={`rounded-lg px-3 py-2 text-sm ${
              testResult.success
                ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400'
                : 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'
            }`}
          >
            {testResult.message}
          </div>
        )}
      </form>
    </Modal>
  );
}

interface DeleteDeviceModalProps {
  isOpen: boolean;
  device: SlideDeviceResponse;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function DeleteDeviceModal({
  isOpen,
  device,
  onClose,
  onConfirm,
  isLoading,
}: DeleteDeviceModalProps) {
  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="button" variant="danger" onClick={onConfirm} loading={isLoading}>
        Delete
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Device"
      subtitle="This action cannot be undone"
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to delete{' '}
        <strong className="text-gray-900 dark:text-white">{device.name}</strong>?
      </p>
    </Modal>
  );
}
